import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Drawer,
  IconButton, ListItemText, Menu, MenuItem, Paper, Skeleton, Stack, TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { Add, Close, Edit, MoreVert, MergeType } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { peopleService, type FamilyDetailResponse, type Guardian } from '../../services/peopleService';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import StatusChip, { ToneChip } from '../common/StatusChip';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { GroupLabel } from '../admissions/tabLayout';
import dateTimeUtils from '../../utils/dateTimeUtils';
import { fmtClassName } from '../../utils/pdf/format';
import GuardianRow from './GuardianRow';
import FamilyChildRow from './FamilyChildRow';
import GuardianDialog from './GuardianDialog';
import MergeFamiliesDialog from './MergeFamiliesDialog';
import { FAMILY_STATUS, familyName } from './familyFormat';
import { profilePath } from '../../utils/personRef';

interface Props {
  familyId: string;
  onClose: () => void;
  /** Something about a family changed — refresh the list. */
  onChanged: () => void;
  /** Show another family (after a merge, or from a CNIC clash). */
  onOpenFamily: (familyId: string) => void;
}

const idOf = (ref: unknown): string => (ref && typeof ref === 'object' ? String((ref as { _id: string })._id) : String(ref ?? ''));

/**
 * One household: its children, guardians and the children's admission
 * applications. Staff with People & Families rights can rename it, manage
 * guardians, choose the fee payer and primary contact, and merge it with
 * another family that turns out to be the same household.
 */
const FamilyDrawer: React.FC<Props> = ({ familyId, onClose, onChanged, onOpenFamily }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const canEdit = canWrite('people');

  const [data, setData] = useState<FamilyDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [guardianMenu, setGuardianMenu] = useState<{ anchor: HTMLElement; guardian: Guardian } | null>(null);
  const [guardianDialog, setGuardianDialog] = useState<{ guardian: Guardian | null } | null>(null);
  const [removing, setRemoving] = useState<Guardian | null>(null);
  // Changing who is billed is confirmed first.
  const [newPayer, setNewPayer] = useState<{ _id: string; name: string } | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await peopleService.getFamily(familyId));
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not load the family', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [familyId, enqueueSnackbar]);
  useEffect(() => { load(); }, [load]);

  const changed = async () => { await load(); onChanged(); };

  const update = async (payload: Parameters<typeof peopleService.updateFamily>[1], done: string) => {
    setBusy(true);
    try {
      await peopleService.updateFamily(familyId, payload);
      enqueueSnackbar(done, { variant: 'success' });
      await changed();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not update the family', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const removeGuardian = async () => {
    if (!removing) return;
    setBusy(true);
    try {
      await peopleService.deleteGuardian(removing._id);
      enqueueSnackbar(`${removing.name} removed`, { variant: 'success' });
      setRemoving(null);
      await changed();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not remove the guardian', { variant: 'error' });
      setRemoving(null);
    } finally {
      setBusy(false);
    }
  };

  const family = data?.family;
  const primaryId = idOf(family?.primaryContactGuardianId);
  const feePayerId = idOf(family?.feePayerGuardianId);
  const status = family ? FAMILY_STATUS[family.status] : null;

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      slotProps={{
        root: { style: { zIndex: 1250 } },
        paper: { style: { zIndex: 1250 }, sx: { width: { xs: '100%', sm: 600, md: 680 }, display: 'flex', flexDirection: 'column' } },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
        {loading && !data ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <Skeleton variant="circular" width={52} height={52} />
            <Box sx={{ flex: 1 }}><Skeleton width={200} height={28} /><Skeleton width={160} height={18} /></Box>
          </Stack>
        ) : family && status && (
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Avatar sx={{ width: 52, height: 52, fontSize: '1.2rem', fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' }}>
              {familyName(family).split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase()).join('')}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="h6" fontWeight={700} noWrap sx={{ lineHeight: 1.2 }}>{familyName(family)}</Typography>
                {canEdit && (
                  <Tooltip title="Rename">
                    <IconButton size="small" onClick={() => setRenaming(family.displayName ?? '')} aria-label="Rename family"><Edit sx={{ fontSize: 16 }} /></IconButton>
                  </Tooltip>
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                <Tooltip title={status.hint}><span><ToneChip label={status.label} tone={status.tone} /></span></Tooltip>
                <Typography variant="body2" color="text.secondary">
                  {data!.people.length} {data!.people.length === 1 ? 'child' : 'children'} · {data!.guardians.length} guardian{data!.guardians.length === 1 ? '' : 's'}
                </Typography>
              </Stack>
            </Box>
            <Stack direction="row" spacing={0.25} sx={{ mt: -0.5, mr: -1 }}>
              {canEdit && (
                <IconButton size="small" onClick={e => setMenuAnchor(e.currentTarget)} aria-label="Family actions"><MoreVert fontSize="small" /></IconButton>
              )}
              <IconButton size="small" onClick={onClose} aria-label="Close"><Close fontSize="small" /></IconButton>
            </Stack>
          </Stack>
        )}
      </Box>

      {/* ── Body ── */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3, bgcolor: alpha(theme.palette.text.primary, 0.015) }}>
        {!data ? (
          <Stack spacing={2}>{[0, 1, 2].map(i => <Skeleton key={i} variant="rectangular" height={64} />)}</Stack>
        ) : (
          <Stack spacing={3}>
            {canEdit && data.possibleDuplicates.length > 0 && (
              <Alert
                severity="warning"
                action={<Button color="inherit" size="small" onClick={() => setMergeOpen(true)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>Review</Button>}
              >
                May be the same household as {data.possibleDuplicates.slice(0, 3).map(m =>
                  `${m.displayName || 'an unnamed family'} (${m.reasons[0].toLowerCase()})`).join(', ')}
                {data.possibleDuplicates.length > 3 ? ` and ${data.possibleDuplicates.length - 3} more` : ''}.
              </Alert>
            )}

            {/* Children */}
            <Box>
              <GroupLabel count={data.people.length}>Children</GroupLabel>
              <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                {data.people.map((p, i) => (
                  <React.Fragment key={p._id}>
                    {i > 0 && <Divider />}
                    <FamilyChildRow person={p} onOpen={() => navigate(profilePath(p))} />
                  </React.Fragment>
                ))}
              </Paper>
            </Box>

            {/* Guardians */}
            <Box>
              <GroupLabel
                count={data.guardians.length}
                action={canEdit && (
                  <Button size="small" startIcon={<Add fontSize="small" />} onClick={() => setGuardianDialog({ guardian: null })} sx={{ textTransform: 'none' }}>
                    Add guardian
                  </Button>
                )}
              >
                Guardians
              </GroupLabel>
              {data.guardians.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No guardians recorded yet. {family?.status === 'candidate' ? 'They are added when the family is linked from the admission application, or you can add them here.' : ''}
                </Typography>
              ) : (
                <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                  {data.guardians.map((g, i) => (
                    <React.Fragment key={g._id}>
                      {i > 0 && <Divider />}
                      <GuardianRow
                        guardian={g}
                        tags={[g._id === primaryId && 'Primary contact', g._id === feePayerId && 'Fee payer'].filter(Boolean) as string[]}
                        actions={canEdit && (
                          <IconButton size="small" onClick={e => setGuardianMenu({ anchor: e.currentTarget, guardian: g })} aria-label={`Actions for ${g.name}`}>
                            <MoreVert fontSize="small" />
                          </IconButton>
                        )}
                      />
                    </React.Fragment>
                  ))}
                </Paper>
              )}
            </Box>

            {/* Admission applications */}
            {data.applications.length > 0 && (
              <Box>
                <GroupLabel count={data.applications.length}>Admission applications</GroupLabel>
                <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                  {data.applications.map((a, i) => {
                    const cycle = typeof a.cycleId === 'object' ? a.cycleId : null;
                    const cls = typeof a.classId === 'object' ? a.classId.name : '';
                    return (
                      <React.Fragment key={a._id}>
                        {i > 0 && <Divider />}
                        <Stack
                          direction="row" spacing={1.5} alignItems="center"
                          onClick={cycle ? () => navigate(`/app/admissions/cycles/${cycle._id}?q=${encodeURIComponent(a.name)}`) : undefined}
                          sx={{ px: 2, py: 1.25, cursor: cycle ? 'pointer' : 'default', '&:hover': cycle ? { bgcolor: 'action.hover' } : undefined }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>{a.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {[cycle?.name, fmtClassName(cls), dateTimeUtils.formatDateForDisplay(a.createdAt)].filter(Boolean).join('  ·  ')}
                            </Typography>
                          </Box>
                          <StatusChip status={{ kind: 'application', value: a.status }} />
                        </Stack>
                      </React.Fragment>
                    );
                  })}
                </Paper>
              </Box>
            )}
          </Stack>
        )}
      </Box>

      {/* ── Menus & dialogs ── */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)} sx={{ zIndex: 1300 }}>
        <MenuItem onClick={() => { setMenuAnchor(null); setMergeOpen(true); }}>
          <MergeType fontSize="small" sx={{ mr: 1.5, color: 'warning.main' }} />
          <ListItemText primary="Merge with another family" secondary="When siblings were entered as separate families" />
        </MenuItem>
      </Menu>

      <Menu anchorEl={guardianMenu?.anchor} open={!!guardianMenu} onClose={() => setGuardianMenu(null)} sx={{ zIndex: 1300 }}>
        {guardianMenu && [
          <MenuItem key="edit" onClick={() => { setGuardianDialog({ guardian: guardianMenu.guardian }); setGuardianMenu(null); }}>Edit details</MenuItem>,
          guardianMenu.guardian._id !== feePayerId && (
            <MenuItem key="payer" disabled={busy} onClick={() => { setNewPayer(guardianMenu.guardian); setGuardianMenu(null); }}>
              Make fee payer
            </MenuItem>
          ),
          guardianMenu.guardian._id !== primaryId && (
            <MenuItem key="primary" disabled={busy} onClick={() => { const g = guardianMenu.guardian; setGuardianMenu(null); update({ primaryContactGuardianId: g._id }, `${g.name} is now the primary contact`); }}>
              Make primary contact
            </MenuItem>
          ),
          <Divider key="d" />,
          (() => {
            const g = guardianMenu.guardian;
            const role = g._id === feePayerId ? 'fee payer' : g._id === primaryId ? 'primary contact' : null;
            return role ? (
              <MenuItem key="remove" disabled>
                <ListItemText primary="Remove" secondary={`Choose another ${role} first`} />
              </MenuItem>
            ) : (
              <MenuItem key="remove" sx={{ color: 'error.main' }} onClick={() => { setRemoving(g); setGuardianMenu(null); }}>Remove</MenuItem>
            );
          })(),
        ]}
      </Menu>

      {guardianDialog && (
        <GuardianDialog
          open
          familyId={familyId}
          guardian={guardianDialog.guardian}
          onClose={() => setGuardianDialog(null)}
          onSaved={async () => { setGuardianDialog(null); await changed(); }}
          onOpenFamily={id => { setGuardianDialog(null); onOpenFamily(id); }}
        />
      )}

      <ConfirmationDialog
        open={!!newPayer}
        onClose={() => setNewPayer(null)}
        onConfirm={async () => { const g = newPayer!; setNewPayer(null); await update({ feePayerGuardianId: g._id }, `${g.name} is now the fee payer`); }}
        title={`Make ${newPayer?.name ?? 'this guardian'} the fee payer?`}
        message={`${newPayer?.name ?? 'They'} will be recorded as the person responsible for this family's fees${data?.guardians.find(g => g._id === feePayerId) ? `, replacing ${data.guardians.find(g => g._id === feePayerId)!.name}` : ''}.`}
        confirmText="Make fee payer"
        confirmColor="primary"
        loading={busy}
      />

      <ConfirmationDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={removeGuardian}
        title={`Remove ${removing?.name ?? 'guardian'}?`}
        message="They will no longer be listed as a guardian of this family."
        confirmText="Remove"
        confirmColor="error"
        loading={busy}
      />

      <Dialog open={renaming !== null} onClose={() => setRenaming(null)} maxWidth="xs" fullWidth sx={{ zIndex: 1300 }}>
        <DialogTitle>Rename family</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Family name" value={renaming ?? ''} onChange={e => setRenaming(e.target.value)} sx={{ mt: 1 }}
            helperText="Usually the father's or the family's surname." />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRenaming(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" disableElevation disabled={!renaming?.trim() || busy} sx={{ textTransform: 'none' }}
            onClick={async () => { const name = renaming!.trim(); setRenaming(null); await update({ displayName: name }, 'Family renamed'); }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {mergeOpen && data && (
        <MergeFamiliesDialog
          open
          family={data}
          onClose={() => setMergeOpen(false)}
          onMerged={kept => { setMergeOpen(false); onChanged(); if (kept === familyId) load(); else onOpenFamily(kept); }}
        />
      )}
    </Drawer>
  );
};

export default FamilyDrawer;
